import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Breadcrumbs, Link, Button, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import AccountList from '../../components/AccountList';
import AccountDetail from '../../components/AccountDetail';
import { AccountService, IAccount } from '../../service/accountService';
import { PiService } from '../../service/piService';
import { RaspberryPi } from '../../model/RaspberryPi';

const AccountsAdmin: React.FC = () => {
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);
  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPi, setLoadingPi] = useState(false);
  const navigate = useNavigate();

  // Accounts beim Laden der Komponente abrufen
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const accounts = await AccountService.getAccounts();
      setAccounts(accounts);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setError("Fehler beim Laden der Accounts.");
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion zum Prüfen und Laden eines Raspberry Pi für einen Account
  const checkAndFetchPi = async (account: IAccount): Promise<IAccount> => {
    console.log('checkAndFetchPi called for account:', account.displayName, 'ID:', account._id);
    console.log('Pi assignment data:', {
      assignedRaspberryPiRef: account.assignedRaspberryPiRef,
      raspberryPi: account.raspberryPi
    });
    
    // Wenn kein Pi zugewiesen ist, Account unverändert zurückgeben
    if (!account.assignedRaspberryPiRef && !account.raspberryPi) {
      console.log('No Pi assignment found for account');
      return account;
    }

    setLoadingPi(true);
    try {
      // Prüfen, ob es sich um eine MongoDB ObjectId handelt
      const piRef = account.assignedRaspberryPiRef;
      if (!piRef) {
        console.log('No piRef found despite assignment flag');
        return account;
      }

      console.log('Fetching Pi details for reference:', piRef);
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(piRef);
      let pi: RaspberryPi;

      if (isValidObjectId) {
        // Wenn es eine ObjectId ist, Pi nach ID laden
        console.log('Loading Pi by ID:', piRef);
        pi = await PiService.getPiById(piRef);
      } else {
        // Wenn nicht, Pi nach Seriennummer laden
        console.log('Loading Pi by serial number:', piRef);
        pi = await PiService.getPiBySerialNumber(piRef);
      }

      console.log('Pi loaded successfully:', pi.name, 'ID:', pi._id);
      
      // Aktualisiere den Account mit den vollständigen Pi-Informationen
      return {
        ...account,
        raspberryPi: pi
      };
    } catch (error) {
      console.error('Error loading Raspberry Pi details:', error);
      // Original-Account zurückgeben, wenn der Pi nicht geladen werden konnte
      return account;
    } finally {
      setLoadingPi(false);
    }
  };

  const handleAccountSelect = async (account: IAccount) => {
    console.log('Account selected:', account.displayName, 'ID:', account._id);
    
    // First check if we need to fetch Pi data directly from the API
    try {
      console.log('Attempting to fetch Pis directly for this account owner');
      const assignedPis = await PiService.getPiForOwner(account._id);
      console.log('Direct Pi lookup result:', assignedPis);
      
      // If we found any Pis assigned to this owner, add this information to the account
      if (assignedPis && assignedPis.length > 0) {
        console.log('Found Pi assigned to account via direct API call:', assignedPis[0].name);
        account = {
          ...account,
          raspberryPi: assignedPis[0], // Use the first Pi (there should only be one)
          assignedRaspberryPiRef: assignedPis[0]._id // Also set the reference
        };
      } else {
        console.log('No Pis found for this account via direct API call');
      }
    } catch (error) {
      console.error('Error fetching Pis directly for account:', error);
    }
    
    // Now continue with checking for existing Pi references
    const accountWithPi = await checkAndFetchPi(account);
    console.log('Setting selected account with Pi data:', accountWithPi.raspberryPi ? 'Pi attached' : 'No Pi attached');
    
    setSelectedAccount(accountWithPi);
  };

  const handleBackClick = () => {
    setSelectedAccount(null);
  };

  const handleAccountUpdate = () => {
    // Accounts neu laden, um aktuelle Daten anzuzeigen
    fetchAccounts();
    
    // Wenn ein Account ausgewählt ist, auch dessen Pi-Details aktualisieren
    if (selectedAccount) {
      handleAccountSelect(selectedAccount);
    }
  };

  const handleAddAccount = () => {
    // Hier könnte später die Logik für das Hinzufügen eines neuen Accounts implementiert werden
    console.log('Neuen Account hinzufügen');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href="/dashboard"
          underline="hover"
        >
          Dashboard
        </Link>
        <Link 
          color="inherit" 
          href="/backoffice"
          underline="hover"
        >
          Administration
        </Link>
        <Typography color="text.primary">Accounts</Typography>
      </Breadcrumbs>

      {/* Seitentitel und Aktionen */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1">
          {selectedAccount ? 'Account-Details' : 'Accounts'}
        </Typography>
        
        {!selectedAccount && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ borderRadius: '8px' }}
            onClick={handleAddAccount}
          >
            Neuer Account
          </Button>
        )}
      </Box>

      {/* Fehlermeldung anzeigen, falls vorhanden */}
      {error && (
        <Typography 
          color="error" 
          sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}
        >
          {error}
        </Typography>
      )}

      {/* Hauptinhalt - Accountliste oder Detailansicht */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : loadingPi ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Raspberry Pi Informationen werden geladen...</Typography>
        </Box>
      ) : selectedAccount ? (
        <AccountDetail 
          account={selectedAccount} 
          onBackClick={handleBackClick} 
          onUpdate={handleAccountUpdate}
        />
      ) : (
        <AccountList 
          accounts={accounts} 
          onSelectAccount={handleAccountSelect} 
        />
      )}
    </Container>
  );
};

export default AccountsAdmin; 